import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ArrowDownTrayIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../../utils/baseUtils';
import { cn } from '../../../components/ui/_cn';
import connectApi from '../../../services/connectApi';
import FirstPayoutBanner from '../../connect/finance/first-payout-banner';
import FinBadge from '../../connect/finance/fin-badge';
import FinButton from '../../connect/finance/fin-button';
import { FinSkeletonLine } from '../../connect/finance/fin-skeleton';
import { payoutTone, wearsOnlyInstant } from '../../connect/finance/payout-activity-table';
import { UPCOMING_PAYOUT_ID } from '../../connect/finance/upcoming-payout';
import COPY from '../../connect/finance/connect-finance-copy';
import { useFinanceSection } from '../section/finance-section-context';
import { downloadCsv } from '../csv';
import FinancePageShell from './finance-page-shell';
import PayoutTimeline, { atZone, payoutZone, stateKey } from './payout-timeline';
import PayoutStatementHelp from './payout-statement-help';
import PayoutIssueCard from './payout-issue-card';
import PayoutSummaryCard from './payout-summary-card';
import PayoutDetailsCard from './payout-details-card';
import PayoutTransactions from './payout-transactions';
import { payoutCsvFilename, payoutDetailCsv } from './payout-detail-export';

dayjs.extend(utc);

// Frame 2c: one payout, and the balance transactions that add up to it. Our
// own page rather than Stripe's payout_details overlay (spec 2026-09-22 §6)
// — the overlay could not be styled, could not be linked to, and showed
// Stripe's own vocabulary to a restaurant.
//
// Connect-only, like Payouts, but open to the accountant too: there is
// nothing to manage here, only figures to read.
//
// A 404 and a failed read are different facts. "This payout isn't in your
// account" is final and offers the way back; any other refusal may pass, so
// it offers a retry instead.
//
// A payout sent by hand — standard on request, or instant — is one Stripe
// will not itemize, and the API says so with `itemized: false` (owner,
// 2026-09-23). The page keeps what is still true of it — the header, the
// timeline, the statement help and the Details card — and puts a note where
// Summary stood, pointing at Order Reports; Summary, Transactions and the
// export would only draw empty groups.
const PayoutDetail = () => {
  const { payoutId } = useParams();
  const { companyId } = useFinanceSection();
  // One Upcoming row opens one arrival-date group (owner, 2026-09-23: the
  // Sep 29 $18.91 row opened a page of every pending dollar). Anything but a
  // plain YYYY-MM-DD is ignored and the page reads everything pending.
  const [searchParams] = useSearchParams();
  const rawArrival = searchParams.get('arrival');
  const arrival = payoutId === UPCOMING_PAYOUT_ID && /^\d{4}-\d{2}-\d{2}$/.test(rawArrival || '')
    ? rawArrival
    : undefined;
  const location = useLocation();
  const copy = COPY.restaurant;

  const [state, setState] = useState({ data: null, error: '', missing: false, loading: true });
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (!companyId || !payoutId) return;
    requestId.current += 1;
    const id = requestId.current;
    setState((prev) => ({ ...prev, loading: true, error: '', missing: false }));
    try {
      // The Upcoming row's own page (spec 2026-09-22 §13): same DTO, own
      // endpoint — it never answers 404 ("missing"), so that state stays
      // unreachable for it below the way it already is for every read that
      // resolves.
      const res = payoutId === UPCOMING_PAYOUT_ID
        ? await connectApi.getUpcomingDetail(companyId, arrival)
        : await connectApi.getPayoutDetail(companyId, payoutId);
      const data = connectApi.toPayoutDetail(res);
      if (!mounted.current || id !== requestId.current) return;
      if (!data) throw new Error('payout-detail');
      setState({ data, error: '', missing: false, loading: false });
    } catch (caught) {
      if (!mounted.current || id !== requestId.current) return;
      const missing = caught?.response?.status === 404;
      setState({
        data: null,
        error: missing ? '' : "Couldn't load this payout.",
        missing,
        loading: false,
      });
    }
  }, [companyId, payoutId, arrival]);

  useEffect(() => { load(); }, [load]);

  // The list's own period survives the trip back, the way the sidebar's
  // finance links already carry it (layout.js's financeSubItemHref).
  const period = new URLSearchParams(location.search).get('period');
  const backTo = period
    ? `/dashboard/finance/payouts?period=${encodeURIComponent(period)}`
    : '/dashboard/finance/payouts';

  // The detail object is flat (Task 3): the payout's own facts sit on it
  // beside its groups. Instants (sent, ended) read on the store's clock, like
  // the timeline beside them; the deposit day is Stripe's calendar day, UTC.
  // The CSV keeps UTC.
  const detail = state.data;
  const zone = payoutZone(detail);
  const longDate = (value) => (value ? dayjs.utc(value).format('dddd, MMMM D') : null);
  const longInstant = (value) => (value ? atZone(value, zone, 'dddd, MMMM D') : null);
  const sent = longInstant(detail?.createdAt);
  // Stripe's status, except that a failed payout the bank sent back after
  // depositing it is "returned" (mock frame 5f; the API tells them apart).
  const key = detail ? stateKey(detail) : '';
  // The header's line under the amount, by state (owner's "Payout States"
  // mock, frames 5a to 5f): initiated while Stripe has not sent it, sent
  // while it travels, deposited on its arrival day once it has landed; the
  // day it failed, was canceled or was returned otherwise.
  const deposited = key === 'paid' ? longDate(detail.arrivalDate) : null;
  const changed = longInstant(detail?.statusAt);
  const ENDED = { failed: 'Failed', canceled: 'Canceled', returned: 'Returned by bank' };
  const headerLine = ENDED[key]
    ? [ENDED[key], changed].filter(Boolean).join(' ')
    : deposited
      ? `Deposited ${deposited}`
      : sent
        ? `${key === 'pending' ? 'Initiated' : 'Sent'} ${sent}`
        : null;
  // A payout that did not simply arrive gets a different right panel: why,
  // and what to do — not help finding a deposit on a statement.
  const issue = ENDED[key] ? key : null;
  // An Upcoming group below zero (owner, 2026-09-23) is no payout: nothing
  // arrives, Stripe takes it from the next sales.
  const shortfall = detail?.status === 'upcoming' && (detail?.amount || 0) < 0;
  const itemized = detail?.itemized !== false;

  const exportCsv = () => downloadCsv(payoutDetailCsv(detail), payoutCsvFilename(payoutId, arrival));

  return (
    <FinancePageShell>
      <Link
        to={backTo}
        className="inline-flex w-max items-center gap-1.5 text-[13px] font-semibold text-fin-muted hover:text-fin-ink"
      >
        <ChevronLeftIcon className="h-3 w-3 stroke-[2.5]" aria-hidden="true" />
        Payouts
      </Link>

      {state.missing ? (
        <div className="rounded-xl border border-fin-border bg-fin-surface px-6 py-8">
          <p className="text-[13.5px] text-fin-ink-2">{copy.payoutMissing}</p>
          <Link
            to="/dashboard/finance/payouts"
            className="mt-3 inline-block text-[13px] font-semibold text-fin-blue hover:text-fin-blue-hover"
          >
            Back to payouts
          </Link>
        </div>
      ) : state.error ? (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-fin-border bg-fin-surface px-6 py-8">
          <p className="text-[13.5px] text-fin-red">{state.error}</p>
          <FinButton onClick={load}>{copy.retry}</FinButton>
        </div>
      ) : state.loading || !detail ? (
        <div className="space-y-5">
          <FinSkeletonLine className="h-9 w-56" />
          <FinSkeletonLine className="h-[240px] w-full" />
          <FinSkeletonLine className="h-[200px] w-full" />
        </div>
      ) : (
        <>
          {/* data-testid, because this header repeats two strings that also
              appear further down the page — the amount (Summary's Total
              Payout) and the status label (the timeline's second step) — and
              the tests have to be able to say which one they mean. */}
          <div data-testid="payout-header" className="flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-baseline gap-1.5">
                  {/* Spec §6's weight, not the mock's 700: `font-extrabold`
                      at 32px with -0.02em tracking. */}
                  {/* A canceled payout's amount is struck through and grey (mock
                      5e): the figure is real, the payout is not. */}
                  <span
                    className={cn(
                      'text-[32px] font-extrabold leading-none tracking-[-0.02em] tabular-nums',
                      key === 'canceled' ? 'text-fin-muted line-through decoration-2' : 'text-fin-ink',
                    )}
                  >
                    {formatCurrency(detail.amount)}
                  </span>
                  <span className="text-[15px] font-semibold text-fin-faintest">
                    {String(detail.currency || 'usd').toUpperCase()}
                  </span>
                </div>
                {/* A payout Stripe has created and not yet sent wears the
                    quiet "Upcoming" badge the mock draws (owner, 2026-10-07:
                    the drawing, not its note), not the amber one: nothing
                    is wrong and nothing is late, and the restaurant has one
                    word for money that has not arrived. */}
                {key === 'pending' ? (
                  <>
                    <FinBadge tone="upcoming" size="lg" />
                    {/* The list row tags a pending instant payout "Pending" +
                        "Instant"; the page carries the same tag (owner ruling 4). */}
                    {detail.method === 'instant' ? <FinBadge tone="instant" size="lg" /> : null}
                  </>
                ) : wearsOnlyInstant(detail) ? (
                  /* A paid instant payout wears only "Instant", as in the
                     list rows (owner ruling 4, SP-1); any other state keeps
                     its status beside the tag. */
                  <FinBadge tone="instant" size="lg" />
                ) : (
                  <>
                    <FinBadge tone={payoutTone(detail.status, detail.returned)} size="lg" />
                    {detail.method === 'instant' ? <FinBadge tone="instant" size="lg" /> : null}
                  </>
                )}
              </div>
              {shortfall ? (
                <p className="mt-2.5 text-[14px] text-fin-muted">{copy.upcomingShortfall}</p>
              ) : detail.status === 'upcoming' ? (
                <p className="mt-2.5 text-[14px] text-fin-muted">
                  {detail.arrivalDate
                    ? copy.upcomingArrivalExpected(dayjs.utc(detail.arrivalDate).format('ddd, MMM D'))
                    : copy.upcomingNotSentYet}
                </p>
              ) : headerLine ? (
                <p className="mt-2.5 text-[14px] text-fin-muted">{headerLine}</p>
              ) : null}
            </div>
            {/* The mock also draws "Report an issue"; there is no flow behind
                it, so it is not built (spec 2026-09-22 §2). No export for a
                payout Stripe did not itemize: the file would be a header
                row and nothing else. */}
            {itemized ? (
              <FinButton onClick={exportCsv}>
                <ArrowDownTrayIcon className="mr-2 h-3.5 w-3.5 stroke-[2.2]" aria-hidden="true" />
                Export CSV
              </FinButton>
            ) : null}
          </div>

          <FirstPayoutBanner />

          <section className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px]">
              <PayoutTimeline detail={detail} />
              {issue ? <PayoutIssueCard detail={detail} state={issue} /> : <PayoutStatementHelp detail={detail} />}
            </div>
          </section>

          <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            {itemized ? (
              <PayoutSummaryCard summary={detail.summary} />
            ) : (
              <section className="rounded-xl border border-fin-border bg-fin-surface px-6 py-[22px]">
                <h2 className="text-[15px] font-bold tracking-[-0.01em] text-fin-ink">Transactions</h2>
                <p className="mt-2 max-w-[560px] text-[13.5px] leading-[1.55] text-fin-muted-2">
                  {copy.payoutNotItemized.before}
                  <Link
                    to="/dashboard/finance/order-reports"
                    className="font-semibold text-fin-blue hover:text-fin-blue-hover"
                  >
                    {copy.payoutNotItemized.linkLabel}
                  </Link>
                  {copy.payoutNotItemized.after}
                </p>
              </section>
            )}
            <PayoutDetailsCard detail={detail} />
          </div>

          {itemized ? <PayoutTransactions detail={detail} /> : null}
        </>
      )}
    </FinancePageShell>
  );
};

export default PayoutDetail;
